// SellerFormWrapper.jsx
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useSellerFormState from './useSellerFormState';
import ObjectForm from './ObjectForm';
import FollowUpForm from './FollowUpForm';
import SpinnerOverlay from '../Spinner/spinneroverlay';
import LoginOverlay from '../LoginOverlay';
import axios from '../../axios';
import './SellerFormWrapper.css';

function SellerFormWrapper() {
  const history = useHistory();
  const location = useLocation();
  const [showTips, setShowTips] = useState(true);
  const [isCreatingAd, setIsCreatingAd] = useState(false);
  const [workflowType, setWorkflowType] = useState('ai'); // 'ai' or 'direct'
  const [showLoginAfterSubmission, setShowLoginAfterSubmission] = useState(false);
  const [formDataForSubmission, setFormDataForSubmission] = useState(null);
  const [imageFilesForSubmission, setImageFilesForSubmission] = useState(null);
  const [apiFormDataForSubmission, setApiFormDataForSubmission] = useState(null);
  const [localIsLoggedIn, setLocalIsLoggedIn] = useState(localStorage.getItem('loggedIn') === 'true');
  const [isSubmittingAfterLogin, setIsSubmittingAfterLogin] = useState(false);

  // Detect workflow type from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const workflow = urlParams.get('workflow');
    if (workflow === 'direct' || workflow === 'ai' || workflow === 'slim') {
      setWorkflowType(workflow);
    }
  }, [location.search]);

  // Update local login state when localStorage changes
  useEffect(() => {
    const updateLoginState = () => {
      const newIsLoggedIn = localStorage.getItem('loggedIn') === 'true';
      setLocalIsLoggedIn(newIsLoggedIn);
      console.log('Login state updated:', newIsLoggedIn);
    };
    
    // Check initial state
    updateLoginState();
    
    // Listen for storage changes (in case user logs in/out in another tab)
    window.addEventListener('storage', updateLoginState);
    
    // Listen for our custom login-state-changed event
    window.addEventListener('login-state-changed', updateLoginState);
    
    return () => {
      window.removeEventListener('storage', updateLoginState);
      window.removeEventListener('login-state-changed', updateLoginState);
    };
  }, []);

  // Added setSellerDataId here
  const {
    object,
    setObject,
    images,
    setImages,
    address,
    formSubmitted,
    setFormSubmitted,
    loading,
    finalSubmit,
    nextQuestion,
    setNextQuestion,
    questionsCount,
    setQuestionsCount,
    questionsAnswers,
    setQuestionsAnswers,
    clearForm,
    handleSubmit,
    handleIntermediateFollowUpSubmit,
    handleFinalFollowUpSubmit,
    setSellerDataId,
    isFinalQuestion,
    propertyType,
    setPropertyType,
    aiSuggestions,
    handleDirectSubmit
  } = useSellerFormState(workflowType);

  // Update isCreatingAd state when a final submit is happening
  useEffect(() => {
    // For AI workflow: loading is active and we're at the final question
    // For direct/slim workflow: loading is active and workflow type is 'direct'/'slim' and form is submitted
    // For post-login submission: isSubmittingAfterLogin is true
    const isAIWorkflowCreatingAd = loading && isFinalQuestion;
    const isDirectWorkflowCreatingAd = loading && (workflowType === 'direct' || workflowType === 'slim') && formSubmitted;
    const isPostLoginCreatingAd = isSubmittingAfterLogin && (workflowType === 'direct' || workflowType === 'slim');
    
    setIsCreatingAd(isAIWorkflowCreatingAd || isDirectWorkflowCreatingAd || isPostLoginCreatingAd);
  }, [loading, isFinalQuestion, workflowType, formSubmitted, isSubmittingAfterLogin]);

  useEffect(() => {
    if (location.state) {
      const {
        object: objectDescription,
        images: imageUrls,
        nextQuestion: nextQ,
        questionsCount: qCount,
        questions_answers: qa,
        sellerDataId,
        property_type
      } = location.state;
  
      if (objectDescription) {
        setObject(objectDescription);
      }
      if (imageUrls) {
        setImages(imageUrls);
      }
      if (nextQ) {
        setNextQuestion(nextQ);
      }
      if (qa) {
        setQuestionsAnswers(qa);
      }
      if (sellerDataId) {
        setSellerDataId(sellerDataId);
      }
      if (property_type) {
        setPropertyType(property_type);
      }
      setFormSubmitted(true);
      if (qCount) {
        setQuestionsCount(qCount + 1);
      }
    }
  }, [
    location,
    setObject,
    setNextQuestion,
    setFormSubmitted,
    setQuestionsCount,
    setQuestionsAnswers,
    setSellerDataId,
    setImages,
    setPropertyType
  ]);
  
  // Simplify progress tracking to just 3 main steps
  const getProgressInfo = () => {
    // Different steps based on workflow type
    const totalSteps = (workflowType === 'direct' || workflowType === 'slim') ? 2 : 3;
    
    // Calculate current progress
    let currentStep = 1; // Start at initial form
    
    if (formSubmitted && workflowType === 'ai') {
      currentStep = 2; // Follow-up questions (only for AI workflow)
    }
    
    if (finalSubmit || (formSubmitted && (workflowType === 'direct' || workflowType === 'slim'))) {
      currentStep = (workflowType === 'direct' || workflowType === 'slim') ? 2 : 3; // Final ad
    }
    
    // Calculate percentage based on total steps
    const progressPercentage = (currentStep / totalSteps) * 100;
    
    // Different step labels based on workflow

    
    return {
      totalSteps,
      currentStep,
      progressPercentage,
      isLastQuestion: isFinalQuestion
    };
  };

  const { 
    totalSteps, 
    currentStep, 
    progressPercentage, 
    isLastQuestion 
  } = getProgressInfo();

  // Use local state for login status instead of reading localStorage directly
  const isLoggedIn = localIsLoggedIn;

  // Wrapper for handleSubmit that checks login status
  const handleSubmitWithAuth = async (formData, imageFiles, apiFormData) => {
    console.log('handleSubmitWithAuth called', { isLoggedIn, workflowType: 'ai' });
    
    if (!isLoggedIn) {
      console.log('User not logged in, storing form data and showing login overlay');
      console.log('Form data to store:', formData);
      console.log('Image files to store:', imageFiles?.length || 0, 'files');
      console.log('API form data to store:', !!apiFormData);
      
      // Store form data and show login overlay
      setFormDataForSubmission(formData);
      setImageFilesForSubmission(imageFiles);
      setApiFormDataForSubmission(apiFormData);
      setShowLoginAfterSubmission(true);
      return;
    }
    
    console.log('User already logged in, proceeding with normal submission');
    // User is logged in, proceed with normal submission
    return handleSubmit(formData, imageFiles, apiFormData);
  };

  // Wrapper for handleDirectSubmit that checks login status
  const handleDirectSubmitWithAuth = async (formData, imageFiles, apiFormData) => {
    console.log('handleDirectSubmitWithAuth called', { isLoggedIn, workflowType });
    
    if (!isLoggedIn) {
      console.log('User not logged in, storing form data and showing login overlay');
      console.log('Form data to store:', formData);
      console.log('Image files to store:', imageFiles?.length || 0, 'files');
      console.log('API form data to store:', !!apiFormData);
      
      // Store form data and show login overlay
      setFormDataForSubmission(formData);
      setImageFilesForSubmission(imageFiles);
      setApiFormDataForSubmission(apiFormData);
      setShowLoginAfterSubmission(true);
      return;
    }
    
    console.log('User already logged in, proceeding with normal submission');
    // User is logged in, proceed with normal submission
    return handleDirectSubmit(formData, imageFiles, apiFormData);
  };

  // Handle successful login after form submission
  const handleLoginSuccess = async (type, id, firstname, lastname) => {
    console.log('Login successful, proceeding with form submission...');
    console.log('Stored form data:', {
      hasFormData: !!formDataForSubmission,
      hasImageFiles: !!imageFilesForSubmission,
      imageFilesCount: imageFilesForSubmission?.length || 0,
      workflowType
    });
    
    // Update app state - these should match what's done in App.js
    localStorage.setItem('loggedIn', 'true');
    localStorage.setItem('userType', type);
    localStorage.setItem('userId', id);
    
    // Update local login state immediately
    setLocalIsLoggedIn(true);
    
    // Store name information if provided
    if (firstname) localStorage.setItem('firstname', firstname);
    if (lastname) localStorage.setItem('lastname', lastname);
    
    // Ensure axios authorization header is set (in case it wasn't set properly during BankID login)
    const authToken = localStorage.getItem('authToken');
    if (authToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
      console.log('Authorization header set for axios');
    }
    
    // Notify App.js about login state change
    window.dispatchEvent(new Event('login-state-changed'));
    
    // Close login overlay
    setShowLoginAfterSubmission(false);
    
    // Check if we have form data to submit
    if (!formDataForSubmission) {
      console.error('No form data found to submit after login');
      alert('Fel: Ingen formulärdata hittades. Vänligen fyll i formuläret igen.');
      return;
    }
    
    // Set flag to show that we're submitting after login
    setIsSubmittingAfterLogin(true);
    
    // Add a small delay to ensure localStorage and axios headers are properly set
    setTimeout(async () => {
      try {
        console.log('Submitting form with stored data...');
        
        // Reconstruct the FormData object instead of reusing the stored one
        // FormData objects can't be reliably reused after being stored
        let newApiFormData = null;
        
        if (formDataForSubmission && (imageFilesForSubmission?.length > 0 || apiFormDataForSubmission)) {
          newApiFormData = new FormData();
          
          // Add all form fields
          Object.keys(formDataForSubmission).forEach(key => {
            newApiFormData.append(key, formDataForSubmission[key]);
            console.log(`Added form field: ${key} = ${formDataForSubmission[key]}`);
          });
          
          // Add files if any
          if (imageFilesForSubmission && imageFilesForSubmission.length > 0) {
            imageFilesForSubmission.forEach((file, index) => {
              newApiFormData.append('files[]', file);
              console.log(`Added file ${index}: ${file.name}`);
            });
          }
        }
        
        // Submit the form with reconstructed data
        if (workflowType === 'direct' || workflowType === 'slim') {
          console.log('Calling handleDirectSubmit...');
          await handleDirectSubmit(formDataForSubmission, imageFilesForSubmission, newApiFormData);
        } else {
          console.log('Calling handleSubmit...');
          await handleSubmit(formDataForSubmission, imageFilesForSubmission, newApiFormData);
        }
        
        console.log('Form submission completed successfully');
        
        // Clear stored form data after successful submission
        setFormDataForSubmission(null);
        setImageFilesForSubmission(null);
        setApiFormDataForSubmission(null);
        setIsSubmittingAfterLogin(false);
        
      } catch (error) {
        console.error('Error submitting form after login:', error);
        console.error('Error details:', error.response?.data || error.message);
        
        // Show a user-friendly error message
        const errorMessage = error.response?.data?.message || error.message || 'Ett okänt fel uppstod';
        alert(`Ett fel uppstod vid skickandet av formuläret: ${errorMessage}. Vänligen försök igen.`);
        
        // Clear the submitting state on error but don't clear form data so user can try again
        setIsSubmittingAfterLogin(false);
      }
    }, 500); // 500ms delay to ensure everything is properly set
  };

  // Helper tips based on current form stage
  const getTips = () => {
    if (!formSubmitted) {
      return [
        "Fyll i så mycket information som möjligt om din bostad",
        "Bilder hjälper till att ge en bättre förståelse av bostaden",
        "Beskriv din bostad med fokus på unika egenskaper och fördelar"
      ];
    } else if (formSubmitted && !finalSubmit) {
      // Property-specific tips
      if (propertyType === "Lägenhet") {
        return [
          "Beskriv lägenhetens läge i huset och utsikt",
          "Nämn avgift och vad som ingår i månadsavgiften",
          "Berätta om bostadsrättsföreningens ekonomi om du vet"
        ];
      } else if (propertyType === "Villa" || propertyType === "Radhus" || propertyType === "Parhus" || propertyType === "Kedjehus") {
        return [
          "Beskriv tomten och eventuella uteplatser",
          "Nämn driftskostnader och eventuella renoveringsbehov",
          "Berätta om närområdet och kommunikationer"
        ];
      } else if (propertyType === "Fritidshus") {
        return [
          "Beskriv närhet till vatten och natur",
          "Nämn om huset är vinterbonat för året-runt-användning",
          "Berätta om vatten- och avloppssituation"
        ];
      } else {
        return [
          "AI-assistenten ställer frågor för att få mer information",
          "Dina svar hjälper till att skapa en bättre annons",
          "Var så specifik som möjligt i dina svar"
        ];
      }
    } else {
      return [
        "Din annons har skapats baserat på informationen du angett",
        "Du kan redigera annonsen vid behov",
        "Kontakta mäklare för nästa steg i processen"
      ];
    }
  };

  // Only show main loading overlay during initial form submission (hide during follow-up)
  // For direct workflow, show spinner during both initial submission and post-login submission
  const shouldShowMainSpinner = (loading && !nextQuestion && workflowType !== 'direct') || isSubmittingAfterLogin;

  return (
    <div className="seller-form-container">
      {/* Overall progress bar */}
      <div className="overall-progress-container">
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progressPercentage}%` }}></div>
        </div>
      </div>

      {/* Helper tips panel - can be toggled */}
      {/* Tips-container borttagen på kundens begäran
      <div className="tips-container">
        <div className="tips-header" onClick={() => setShowTips(!showTips)}>
          <h3>Tips och råd</h3>
          <span>{showTips ? '▼' : '▶'}</span>
        </div>
        {showTips && (
          <div className="tips-content">
            <ul>
              {getTips().map((tip, index) => (
                <li key={index}>{tip}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
      */}

      {/* Main form content */}
      <div className="form-content">
        {(!formSubmitted || !nextQuestion || workflowType === 'direct' || workflowType === 'slim') && (
          <ObjectForm
            handleSubmit={(workflowType === 'direct' || workflowType === 'slim') ? handleDirectSubmitWithAuth : handleSubmitWithAuth}
            object={object}
            setObject={setObject}
            images={images}
            setImages={setImages}
            workflowType={workflowType}
          />
        )}

        {formSubmitted && nextQuestion && !finalSubmit && workflowType === 'ai' && (
          <FollowUpForm
            object={object}
            images={images}
            address={address}
            handleIntermediateFollowUpSubmit={handleIntermediateFollowUpSubmit}
            handleFinalFollowUpSubmit={handleFinalFollowUpSubmit}
            nextQuestion={nextQuestion}
            questions_answers={questionsAnswers}
            clearForm={clearForm}
            questionsCount={questionsCount}
            isFinalQuestion={isFinalQuestion}
            propertyType={propertyType}
            aiSuggestions={aiSuggestions}
          />
        )}

        {shouldShowMainSpinner && <SpinnerOverlay creatingAd={isCreatingAd} />}
        
        {/* Login overlay for authentication after form submission */}
        {showLoginAfterSubmission && (
          <LoginOverlay
            onLogin={handleLoginSuccess}
            onClose={() => {
              setShowLoginAfterSubmission(false);
              // Clear stored form data if user cancels login
              setFormDataForSubmission(null);
              setImageFilesForSubmission(null);
              setApiFormDataForSubmission(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

export default SellerFormWrapper;
